/**
 * Contato compartilhado (vCard) — validação e preview para o composer.
 * Paridade com supabase/functions/_shared/whatsapp-contact-message.ts
 */

import { normalizePhoneDigits } from "@/lib/format-phone";

export interface FabContactCanonical {
  name: string;
  phones: string[];
  emails?: string[];
  org?: string | null;
}

function normalizeContactPhones(phones: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of phones) {
    const digits = normalizePhoneDigits(String(raw || "").trim());
    if (!digits || seen.has(digits)) continue;
    seen.add(digits);
    out.push(digits);
  }
  return out;
}

export function validateFabContactCanonical(contact: FabContactCanonical | null | undefined): string | null {
  if (!contact || typeof contact !== "object") return "Contato inválido.";
  const name = String(contact.name || "").trim();
  if (!name) return "Informe o nome do contato.";
  if (name.length > 256) return "Nome do contato muito longo (máx. 256 caracteres).";
  const phones = normalizeContactPhones(Array.isArray(contact.phones) ? contact.phones : []);
  if (phones.length === 0) return "Informe ao menos um telefone válido.";
  if (phones.length > 5) return "Máximo de 5 telefones por contato.";
  const emails = Array.isArray(contact.emails) ? contact.emails : [];
  for (const email of emails) {
    const value = String(email || "").trim();
    if (!value) continue;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "E-mail inválido.";
  }
  const org = String(contact.org || "").trim();
  if (org.length > 256) return "Nome da empresa muito longo (máx. 256 caracteres).";
  return null;
}

export function sanitizeFabContactCanonical(contact: FabContactCanonical): FabContactCanonical {
  const phones = normalizeContactPhones(contact.phones);
  const emails = (contact.emails || [])
    .map((e) => String(e || "").trim())
    .filter(Boolean)
    .slice(0, 3);
  const org = String(contact.org || "").trim() || null;
  return {
    name: String(contact.name || "").trim(),
    phones,
    ...(emails.length > 0 ? { emails } : {}),
    ...(org ? { org } : {}),
  };
}

export function buildContactPreviewBody(contact: FabContactCanonical): string {
  const safe = sanitizeFabContactCanonical(contact);
  const phonePart = safe.phones.length > 0 ? `: ${safe.phones.join(", ")}` : "";
  return `${safe.name}${phonePart}`;
}

export function buildContactPreviewMetadata(contact: FabContactCanonical) {
  const safe = sanitizeFabContactCanonical(contact);
  const nameParts = safe.name.split(/\s+/).filter(Boolean);
  const nameObj = {
    formatted_name: safe.name,
    ...(nameParts[0] ? { first_name: nameParts[0] } : {}),
    ...(nameParts.length > 1 ? { last_name: nameParts.slice(1).join(" ") } : {}),
  };
  return {
    type: "contacts",
    contacts: [
      {
        name: nameObj,
        phones: safe.phones.map((phone) => ({ phone, wa_id: phone })),
        emails: (safe.emails || []).map((email) => ({ email })),
        org: safe.org ? { company: safe.org } : null,
      },
    ],
  };
}
